import { useState } from "react";
import { CreditCard, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Pill } from "@/components/common/States";
import { gbp, daysLeft, useWallet } from "@/components/billing/Credit";
import { AutoTopup, PlanPicker } from "@/components/billing/Plans";
import { useFetch } from "@/lib/hooks";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";

const STATUS = { trialing: ["Free trial", "cyan"], active: ["Active", "teal"], past_due: ["Payment failed", "rose"], canceled: ["Cancelled", "slate"], unpaid: ["Unpaid", "rose"] };
const KIND = { trial_credit: "Trial credit", monthly_allowance: "Monthly included credit", topup: "Top-up", usage: "Research usage", admin_adjustment: "Adjustment" };

function usePay() {
  const { api } = useWorkspace();
  const [busy, setBusy] = useState(null);
  const go = async (key, path, body = {}) => {
    setBusy(key);
    try { const { data } = await api.post(path, { origin_url: window.location.origin, ...body }); window.location.href = data.checkout_url || data.url; }
    catch (e) { toast.error(errorMessage(e)); setBusy(null); }
  };
  return [busy, go];
}

function PlanCard({ w, canManage, onChanged }) {
  const [busy, go] = usePay();
  const [label, tone] = STATUS[w.status] || [w.status, "slate"];
  const trial = w.status === "trialing" && !w.has_subscription;
  return (
    <div className="panel space-y-4 p-6" data-testid="billing-plan">
      <div className="flex items-center justify-between"><p className="font-display text-lg">{w.plan_name}</p><Pill tone={tone} testid="billing-status">{label}</Pill></div>
      <p className="text-sm text-muted-foreground">{gbp(w.monthly_fee_gbp)} a month (excl. VAT), including {gbp(w.included_credit_gbp)} of research credit each month. Extra research uses top-up credit.</p>
      {trial && <p className="text-sm" data-testid="billing-trial">Free trial: <b>{daysLeft(w.trial_ends_at)} days left</b> (ends {new Date(w.trial_ends_at).toLocaleDateString("en-GB")}). Subscribing now keeps the rest of your trial — the first payment is taken when it ends.</p>}
      {w.has_subscription && w.current_period_end && <p className="text-sm" data-testid="billing-renews">{w.cancel_at_period_end ? "Ends" : "Renews"} on {new Date(w.current_period_end).toLocaleDateString("en-GB")}</p>}
      {!w.can_spend && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive" data-testid="billing-blocked">{w.blocked_reason}</p>}
      <PlanPicker w={w} canManage={canManage} go={go} busy={busy} onChanged={onChanged} />
      {canManage ? (w.has_subscription && !["canceled", "incomplete_expired"].includes(w.status) &&
          <Button variant="outline" onClick={() => go("portal", "/billing/portal")} disabled={!!busy} data-testid="billing-manage">{busy === "portal" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1.5 h-4 w-4" />}Manage card, invoices or cancel</Button>)
        : <p className="text-xs text-muted-foreground">Only an Organisation Admin can change the subscription or buy credit.</p>}
    </div>
  );
}

function CreditCardPanel({ w, canManage, onChanged }) {
  const [busy, go] = usePay();
  return (
    <div className="panel space-y-4 p-6" data-testid="billing-credit">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Research credit</p>
      <p className="font-display text-4xl" data-testid="billing-balance">{gbp(w.balance_gbp)}</p>
      <p className="text-sm text-muted-foreground">Every search and AI step uses credit as it runs. When credit runs out, new research pauses until you top up — nothing goes over.</p>
      {canManage && <div className="flex flex-wrap gap-2">{w.topup_packs_gbp.map((p) => (
        <Button key={p} variant="outline" disabled={!!busy} onClick={() => go(p, "/billing/topup", { pack_gbp: p })} data-testid={`billing-topup-${p}`}>
          {busy === p ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Top up {gbp(p)}<span className="ml-1 text-xs text-muted-foreground">+VAT</span></Button>))}</div>}
      <AutoTopup key={JSON.stringify(w.auto_topup)} w={w} canManage={canManage} onChanged={onChanged} />
    </div>
  );
}

function Ledger() {
  const { data } = useFetch("/billing/ledger");
  return (
    <div className="panel overflow-x-auto p-0" data-testid="billing-ledger">
      <p className="border-b p-4 font-medium">Credit history</p>
      <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs uppercase text-muted-foreground"><th className="p-3">Date</th><th>Item</th><th className="text-right">Amount</th><th className="p-3 text-right">Balance</th></tr></thead>
        <tbody>{(data || []).map((r) => (
          <tr key={r.id} className="border-b border-border/60" data-testid={`ledger-row-${r.id}`}>
            <td className="whitespace-nowrap p-3 text-xs">{new Date(r.created_at).toLocaleString("en-GB")}</td>
            <td>{KIND[r.kind] || r.kind}{r.note && r.kind !== "usage" ? <span className="text-xs text-muted-foreground"> · {r.note}</span> : null}</td>
            <td className={`whitespace-nowrap text-right font-mono ${r.amount_gbp < 0 ? "" : "text-emerald-600 dark:text-emerald-400"}`}>{r.amount_gbp < 0 ? "−" : "+"}£{Math.abs(r.amount_gbp).toFixed(r.kind === "usage" ? 4 : 2)}</td>
            <td className="whitespace-nowrap p-3 text-right font-mono">{gbp(r.balance_after_gbp)}</td></tr>))}
          {data && !data.length && <tr><td className="p-4 text-muted-foreground" colSpan={4}>No credit activity yet.</td></tr>}</tbody></table>
    </div>
  );
}

export default function Billing() {
  const { can } = useWorkspace();
  const { data: w, reload } = useWallet();
  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader eyebrow="Account" title="Billing" description="Your subscription, research credit and credit history." />
      {!w ? <Loader2 className="h-5 w-5 animate-spin" /> : w.billing_exempt
        ? <div className="panel p-6 text-sm" data-testid="billing-exempt">This organisation is not billed — research is unlimited.</div>
        : <div className="grid gap-5 lg:grid-cols-2"><PlanCard w={w} canManage={can("manage")} onChanged={reload} /><CreditCardPanel w={w} canManage={can("manage")} onChanged={reload} /></div>}
      <Ledger />
    </div>
  );
}
